// src/components/roster/PlayerAvatar.jsx
//
// Player avatar for roster / teammates cards. Three states:
//   1. Photo present  → circular headshot + jersey-number badge bottom-right
//   2. No photo, has jersey → lime jersey-number circle (legacy look)
//   3. No photo, no jersey → zinc initials circle
//
// Photo source: the player's `photoURL` (copied from their registration's
// uploaded player_photo when a coach adds them to the roster). Broken /
// expired URLs fall back to the jersey circle via onError.
//
// Badge sits bottom-right overlapping the photo edge — the sports-card
// convention; it avoids the face (which centers in a headshot) and stays
// clear of the name text beside the avatar.

import React, { useState } from 'react';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function PlayerAvatar({
  photoURL,
  jerseyNumber,
  firstName,
  lastName,
  size = 44,            // px; default matches the existing h-11 w-11 footprint
  alt,
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = !!photoURL && !imgFailed;

  const initials = `${(firstName || '?')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  const badgeSize = Math.round(size * 0.42);

  // ── Photo + jersey badge ──────────────────────────────────────────
  if (showPhoto) {
    return (
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <img
          src={photoURL}
          alt={alt || `${firstName || ''} ${lastName || ''}`.trim() || 'Player'}
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="h-full w-full rounded-full object-cover ring-1 ring-zinc-200 bg-zinc-100"
        />
        {jerseyNumber != null && jerseyNumber !== '' && (
          <span
            className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-[#b5f03d] text-[#0d1f14] ring-2 ring-white leading-none"
            style={{
              minWidth: badgeSize,
              height: badgeSize,
              padding: '0 4px',
              fontFamily: HEAD_FONT,
              fontWeight: 800,
              fontSize: Math.round(badgeSize * 0.6),
            }}
          >
            {jerseyNumber}
          </span>
        )}
      </div>
    );
  }

  // ── No photo: jersey circle or initials ───────────────────────────
  const hasJersey = jerseyNumber != null && jerseyNumber !== '';
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full ${
        hasJersey
          ? 'bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#3d6010]'
          : 'bg-zinc-100 text-zinc-400'
      }`}
      style={{ width: size, height: size, fontFamily: HEAD_FONT, fontWeight: 800, fontSize: Math.round(size * 0.36) }}
    >
      {hasJersey ? `#${jerseyNumber}` : initials}
    </div>
  );
}
